import Button from '@doordash/component-button'
import {
  EmailField,
  ErrorMessage,
  PhoneField,
  TextField,
} from '@doordash/component-fields'
import { StackChildren, Text } from '@doordash/design-language'
import { Interpolate } from '@doordash/lib-i18n'
import { CountryCode as PhoneCountryCode } from 'libphonenumber-js'
import * as React from 'react'
import { useCallback, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import {
  createOnboardingRequest,
  submitDasherApplication,
} from '../../../actions'
import {
  ApplicantData,
  ApplicationSubmissionFailurePayload,
  ApplicationSubmissionSuccessPayload,
} from '../../../api'
import { CreateOnboardingRequest } from '../../../api/workflow'
import useWindowKeyDown from '../../../hooks/useWindowKeyDown'
import i18n from '../../../i18n'
import {
  AlreadyStartedEnum,
  AlreadyStartedSigningUpModalProps,
  SupportedCountryCode,
} from '../../../models/types'
import { getOnboardingConfig } from '../../../utilities/onboardingConfig'
import { getOrderedPhoneCountryCodes } from '../../../utilities/phoneNumber'
import { pageLoad, Steps } from '../../../utilities/steps'
import { validators } from '../../../utilities/validators'
import Disclaimer from '../../molecules/disclaimer'
import PageTitle from '../../molecules/page-title'
import Navbar from '../../organisms/navbar'
import ResponsiveNarrow from '../../templates/responsive-narrow'
import StartedSigningUpModal from '../detail-signup/components/StartedSigningUpModal'
import t from './strings'

const defaultPreferredCountryCodes: SupportedCountryCode[] = ['US']

type SimpleSignUpPageSteps = 'email' | 'zipAndPhone'

export type Mode = 'default' | 'v2'

type SimpleSignUpProps = {
  /** Renders a disclaimer below the email form if provided */
  disclaimer?: JSX.Element
  /**
   * Urls where Terms of agreement and Privacy policy are hosted.
   * If both are provided, shows an extra disclaimer above the submit button.
   */
  termsOfAgreementUrl?: string
  privacyPolicyUrl?: string
  /** The initial step of the page */
  initialStep?: SimpleSignUpPageSteps
  /** Will initially populate the email field if provided */
  prefilledEmail?: string
  /** Will initially select the country code, for phone number */
  preselectedCountryCode: SupportedCountryCode
  /** Used to validate zipcodes */
  applicantCountryCode: SupportedCountryCode
  /**
   * v1 API invoked when sign up form is submitted.
   */
  submitDasherApplication: (
    data: ApplicantData
  ) => Promise<ApplicationSubmissionSuccessPayload>
  /**
   * v2 API invoked when sign up form is submitted.
   */
  createOnboarding: (data: CreateOnboardingRequest) => { uniqueLink: string }
  /**
   * Cb invoked when user indicated they've already began signing up
   * When defined, will display a button on the email input screen
   * that can be clicked to trigger the request.
   */
  onExistingUserRequest?: (options?: AlreadyStartedSigningUpModalProps) => void
  /**
   * Cb invoked when user clicks to close the StartedSigningUpModal
   */
  onExistingUserModalCloseRequest?: () => void
  /**
   * Used to determine whether the StartedSigningUpModal is open or not
   */
  isExistingUserModalOpen?: boolean
  /**
   * Mode is used to switch between the default flow and v2 flow
   */
  mode?: Mode
}

/**
 * Page which prompts for an email input, to start the sign up process,
 * then accepts zipCode & phone numbers.
 *
 * Has 2 SignUpPageSteps:
 * 1. email: accept a valid email from the user
 * 2. zipAndPhone: accept a zip code and phone number from the user
 *
 * The page defaults to be for a US applicant. Control locales with applicantCountryCode
 * and preselectedCountryCode. Note that applicantCountryCode applies strict
 * validations for zipCodes, while the preselectedCountryCode provides sensible
 * defaults for the phone number's country code.
 *
 * Use the onSubmit cb to do something with the data.
 */
const SimpleSignUpPage: React.FC<SimpleSignUpProps> = ({
  disclaimer,
  termsOfAgreementUrl,
  privacyPolicyUrl,
  initialStep = 'email',
  prefilledEmail = '',
  preselectedCountryCode,
  applicantCountryCode,
  onExistingUserRequest,
  onExistingUserModalCloseRequest,
  isExistingUserModalOpen,
  createOnboarding,
  submitDasherApplication,
  mode = 'default',
}) => {
  const [step, setStep] = useState<SimpleSignUpPageSteps>(initialStep)

  const [email, setEmail] = useState<string>(prefilledEmail)
  const [emailErrorMsg, setEmailErrorMsg] = useState<string>('')

  const [zipCode, setZipCode] = useState<string>('')
  const [zipCodeErrorMsg, setZipCodeErrorMsg] = useState<string>('')

  const [countryCode, setCountryCode] = useState<PhoneCountryCode>(
    preselectedCountryCode
  )
  const [displayedPhoneNumber, setDisplayedPhoneNumber] = useState<string>('')
  // E164 format, for actual submission
  const [formattedPhoneNumber, setFormattedPhoneNumber] = useState<string>('')
  const [phoneNumberErrorMsg, setPhoneNumberErrorMsg] = useState<string>('')

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [submissionErrorMsg, setSubmissionErrorMsg] = useState<string>('')

  const employmentType = getOnboardingConfig(
    'signupPage',
    'employmentType',
    applicantCountryCode
  )

  const handleEmailFieldChange = (value: string) => {
    if (emailErrorMsg) {
      setEmailErrorMsg('')
    }
    setEmail(value)
  }

  const handleEmailSubmit = useCallback(() => {
    const emailError = validators.email(email)

    if (emailError) {
      setEmailErrorMsg(emailError)

      // Do not continue
      return
    }

    /**
     * IMPROVE @peterj35-dd: In order to greatly enhance user experience, here
     * a request to the backend should be made to check if the email already
     * exists, in order to short circuit that validation.
     *
     * Currently this check if only finally done after the user has
     * filled out rest of the form, and submitted it.
     */
    setStep('zipAndPhone')
  }, [email])

  const handleExistingUserClick = () => {
    if (onExistingUserRequest) {
      onExistingUserRequest({
        email,
        phoneNumber: displayedPhoneNumber,
      })
    }
  }

  const handleBackFromZipAndPhone = () => {
    setStep('email')
  }

  const handleZipCodeChange = (value: string) => {
    if (zipCodeErrorMsg) {
      setZipCodeErrorMsg('')
    }
    setZipCode(value)
  }

  const handleContryCodeChange = (value: string) => {
    setCountryCode(value as PhoneCountryCode)
  }

  const handlePhoneNumberChange = (
    unformatted: string,
    formatted: { number: { number: string } }
  ) => {
    if (phoneNumberErrorMsg) {
      setPhoneNumberErrorMsg('')
    }
    setDisplayedPhoneNumber(unformatted)
    setFormattedPhoneNumber(formatted.number.number)
  }

  const callCreateOnboarding = useCallback(async () => {
    const payload = {
      geoLocator: { zip: zipCode, country: applicantCountryCode },
      personLocator: { email, phoneNumber: formattedPhoneNumber },
    }
    try {
      const result = await createOnboarding(payload)

      pageLoad(result.uniqueLink, Steps.StartOnBoardingWorkflow)
    } catch (e) {
      /**
       * TODO: Handle validations for already existing phone number and email etc.
       * once the API starts returning error messages
       */
      setSubmissionErrorMsg(t.genericError())
      setIsSubmitting(false)
      // Rethrow since this is an unexpected response
      throw e
    }
  }, [
    applicantCountryCode,
    createOnboarding,
    email,
    formattedPhoneNumber,
    zipCode,
  ])

  const callSubmitDasherAppication = useCallback(async () => {
    const payload = { email, zipCode, phoneNumber: formattedPhoneNumber }
    try {
      const result = await submitDasherApplication(payload)

      pageLoad(result.uniqueLink, Steps.ProfileSetup)
    } catch (e) {
      if (!e.fieldErrors) {
        setSubmissionErrorMsg(t.genericError())
        setIsSubmitting(false)
        // Rethrow since this is an unexpected response
        throw e
      }
      const error = e as ApplicationSubmissionFailurePayload

      const {
        phoneNumber: phoneNumberError,
        email: emailError,
        zipCode: zipError,
        detail: detailError,
      } = error.fieldErrors

      if (phoneNumberError || emailError) {
        /**
         * Make a dubious assumption that ALL errors related
         * to phone numbers or emails must be due to there
         * being an existing user
         *
         * IMPROVE @peterj35-dd: At the moment, because the only error
         * payload is a translated message, it is infeasible to try and parse
         * the message to understand the failure.
         *
         * The API should return an error code such as 422 for invalid data,
         * and 409 if the entity already existed, and those codes should be
         * used to show different error states to the user.
         */
        onExistingUserRequest({
          email,
          phoneNumber: displayedPhoneNumber,
          type: AlreadyStartedEnum.ExistingCredentials,
        })
      }

      if (phoneNumberError) {
        setPhoneNumberErrorMsg(phoneNumberError)
      }
      if (emailError) {
        setEmailErrorMsg(emailError)
        // Also set as a submission error as the email error is only shown on previous page
        setSubmissionErrorMsg(emailError)
      }
      if (zipError) {
        setZipCodeErrorMsg(zipError)
      }
      if (detailError) {
        setSubmissionErrorMsg(detailError)
      }
    }
    setIsSubmitting(false)
  }, [
    displayedPhoneNumber,
    email,
    formattedPhoneNumber,
    onExistingUserRequest,
    submitDasherApplication,
    zipCode,
  ])

  const handleSignupSubmit = useCallback(() => {
    setSubmissionErrorMsg('')
    setIsSubmitting(true)

    const zipCodeError = validators.zipCode(
      zipCode,
      undefined,
      undefined,
      applicantCountryCode
    )
    if (zipCodeError) {
      setZipCodeErrorMsg(zipCodeError)
    }

    const phoneNumberError = validators.phoneNumber(formattedPhoneNumber)
    if (phoneNumberError) {
      setPhoneNumberErrorMsg(phoneNumberError)
    }

    if (zipCodeError || phoneNumberError) {
      // Do not submit
      setIsSubmitting(false)
      return
    }

    if (mode === 'v2') {
      callCreateOnboarding()
    } else {
      callSubmitDasherAppication()
    }
  }, [
    zipCode,
    applicantCountryCode,
    formattedPhoneNumber,
    mode,
    callCreateOnboarding,
    callSubmitDasherAppication,
  ])

  const handleEnterKeyDown = useCallback(() => {
    if (step === 'email') {
      handleEmailSubmit()
      return
    }

    handleSignupSubmit()
  }, [step, handleSignupSubmit, handleEmailSubmit])

  useWindowKeyDown('Enter', handleEnterKeyDown)

  const orderedPhoneFieldCountries: string[] = useMemo(
    () =>
      getOrderedPhoneCountryCodes({
        preferredCountryCodes: [
          preselectedCountryCode,
          ...defaultPreferredCountryCodes,
        ],
        includeAllCountries: true,
      }),
    [preselectedCountryCode]
  )

  return (
    <>
      {isExistingUserModalOpen ? (
        <StartedSigningUpModal
          onClose={onExistingUserModalCloseRequest}
          phoneCountry={countryCode}
          includeAllCountryCodes
          employmentType={employmentType}
        />
      ) : null}
      <div>
        <Navbar />
        <ResponsiveNarrow>
          <PageTitle>{t.getStarted()}</PageTitle>
          {step === 'email' ? (
            <StackChildren size={StackChildren.Sizes.Large}>
              <EmailField
                value={email}
                onChange={handleEmailFieldChange}
                label={t.emailAddress()}
                error={emailErrorMsg}
                autoFocus
              />
              {disclaimer ? <Disclaimer>{disclaimer}</Disclaimer> : null}
              {termsOfAgreementUrl && privacyPolicyUrl ? (
                <Text
                  color={Text.Colors.TextTertiary}
                  styles={Text.Styles.Caption1}
                >
                  <Interpolate
                    i18n={i18n}
                    i18nKey={t.toaDisclaimer()}
                    components={[
                      // Disable due to false positives
                      /* eslint-disable-next-line jsx-a11y/anchor-has-content */
                      <a href={termsOfAgreementUrl} target="_blank">
                        Terms of agreement
                      </a>,
                      /* eslint-disable-next-line jsx-a11y/anchor-has-content */
                      <a href={privacyPolicyUrl} target="_blank">
                        Privacy policy
                      </a>,
                    ]}
                  />
                </Text>
              ) : null}
              <StackChildren size={StackChildren.Sizes.XxSmall}>
                <Button size={Button.Sizes.Medium} onClick={handleEmailSubmit}>
                  {t.next()}
                </Button>
                {onExistingUserRequest ? (
                  <Button
                    size={Button.Sizes.Medium}
                    type={Button.Types.Tertiary}
                    onClick={handleExistingUserClick}
                  >
                    {t.alreadyStartedSigningUp()}
                  </Button>
                ) : null}
              </StackChildren>
            </StackChildren>
          ) : step === 'zipAndPhone' ? (
            <StackChildren size={StackChildren.Sizes.XxLarge}>
              <StackChildren size={StackChildren.Sizes.Medium}>
                <TextField
                  label={t.zipCode()}
                  value={zipCode}
                  onChange={handleZipCodeChange}
                  error={zipCodeErrorMsg}
                  autoFocus
                />
                <PhoneField
                  label={t.phoneNumber()}
                  countryLabel={t.countryLabel()}
                  value={displayedPhoneNumber}
                  onChange={handlePhoneNumberChange}
                  currentCountry={countryCode}
                  isInternationalEnabled
                  onCountryChange={handleContryCodeChange}
                  error={phoneNumberErrorMsg}
                  availableCountries={orderedPhoneFieldCountries}
                />
              </StackChildren>
              <StackChildren size={StackChildren.Sizes.XxSmall}>
                {submissionErrorMsg ? (
                  <ErrorMessage error={submissionErrorMsg} />
                ) : null}
                <Button
                  size={Button.Sizes.Medium}
                  onClick={handleSignupSubmit}
                  state={
                    isSubmitting ? Button.States.Loading : Button.States.Default
                  }
                >
                  {t.continue()}
                </Button>
                <Button
                  size={Button.Sizes.Medium}
                  type={Button.Types.Tertiary}
                  onClick={handleBackFromZipAndPhone}
                >
                  {t.back()}
                </Button>
              </StackChildren>
            </StackChildren>
          ) : null}
        </ResponsiveNarrow>
      </div>
    </>
  )
}

const mapDispatchToProps = {
  createOnboarding: createOnboardingRequest,
  submitDasherApplication,
}

export default connect(null, mapDispatchToProps)(SimpleSignUpPage)
